/*
Numbered page links under a feed: ‹ 1 … 4 5 6 … 31 ›.
The server renders them (partials/pager.hbs) for readers without JS and for
crawlers, without the "…" marks; this module redraws them with the marks for the
page in the address bar.
Requires: `nav.th-pager` with data-pages (page count) and data-first-url (page 1).
*/

// Page numbers shown on each side of the current one.
const NEIGHBOURS = 1;

export default function pageNav() {
    renderPageNav(pageNumberOf(location.href));
}

/** Redraws the page links with `current` marked; does nothing on a page without them. */
function renderPageNav(current) {
    const nav = document.querySelector('.th-pager');
    if (!nav) {
        return;
    }
    const total = Number(nav.dataset.pages);
    const firstUrl = nav.dataset.firstUrl;
    const items = [];

    if (current > 1) {
        items.push(stepLink(pageUrl(firstUrl, current - 1), '‹', 'Previous page'));
    }
    pageNumbers(current, total).forEach((number) => {
        items.push(pageItem(number, current, firstUrl));
    });
    if (current < total) {
        items.push(stepLink(pageUrl(firstUrl, current + 1), '›', 'Next page'));
    }
    nav.replaceChildren(...items);
}

/**
 * Numbers to show for page `current` of `total`, null where a run of pages is skipped:
 * the first, the last and the neighbours of the current one. A gap of a single page
 * shows that page instead — "…" would take the same room.
 * @param {number} current
 * @param {number} total
 * @returns {Array<number|null>} e.g. (5, 31) → [1, null, 4, 5, 6, null, 31]
 */
export function pageNumbers(current, total) {
    const wanted = new Set([1, total]);

    for (let number = current - NEIGHBOURS; number <= current + NEIGHBOURS; number++) {
        wanted.add(number);
    }
    const shown = [...wanted]
        .filter((number) => number >= 1 && number <= total)
        .sort((a, b) => a - b);
    const result = [];

    shown.forEach((number, index) => {
        const skipped = number - (shown[index - 1] ?? number) - 1;

        if (skipped === 1) {
            result.push(number - 1);
        } else if (skipped > 1) {
            result.push(null);
        }
        result.push(number);
    });
    return result;
}

/** Page number in a list address: /news/page/3/ → 3, /news/ → 1. */
function pageNumberOf(url) {
    const match = new URL(url, location.href).pathname.match(/\/page\/(\d+)\/?$/);

    return match ? Number(match[1]) : 1;
}

// Ghost's own scheme (core/frontend/meta/paginated-url.js): page 1 is the list address.
function pageUrl(firstUrl, number) {
    return number === 1 ? firstUrl : `${firstUrl.replace(/\/$/, '')}/page/${number}/`;
}

function pageItem(number, current, firstUrl) {
    if (number === null) {
        const gap = document.createElement('span');

        gap.className = 'th-pager__gap';
        gap.textContent = '…';
        return gap;
    }
    if (number === current) {
        const here = document.createElement('span');

        here.className = 'th-pager__page';
        here.setAttribute('aria-current', 'page');
        here.textContent = String(number);
        return here;
    }
    const link = document.createElement('a');

    link.className = 'th-pager__page';
    link.href = pageUrl(firstUrl, number);
    link.textContent = String(number);
    return link;
}

function stepLink(href, symbol, label) {
    const link = document.createElement('a');

    link.className = 'th-pager__step';
    link.href = href;
    link.setAttribute('aria-label', label);
    link.textContent = symbol;
    return link;
}
